import { useEffect, useRef } from 'react';

import type { IModalSharedProps } from '../../types/modal';
import { useClickOutside } from '../../hooks/useClickOutside';
import customEvents from '../../utils/customEvents';
import { ModalHeader } from './ModalHeader';
import { ModalFooter } from './ModalFooter';

import { Xmark } from './../../assets/Icons';

import styles from './modal.module.scss';

let sharedOpenModalCount = 0;
let sharedPreviousOverflow: string | null = null;

const lockDocumentScroll = () => {
  if (sharedOpenModalCount === 0) {
    sharedPreviousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }
  sharedOpenModalCount += 1;
};

const unlockDocumentScroll = () => {
  if (sharedOpenModalCount === 0) return;

  sharedOpenModalCount -= 1;
  if (sharedOpenModalCount === 0) {
    document.documentElement.style.overflow = sharedPreviousOverflow ?? '';
    sharedPreviousOverflow = null;
  }
};

export const Modal = (props: IModalSharedProps) => {

  const {
    wrapperClassName,
    state = 'default',
    position = 'default',
    open = false,
    width = '35rem',
    maxWidth,
    isMobile = false,
    hasCloseIcon,
    onCloseHandler,
    closeOnClickOutside = true,
    headerIcon,
    title,
    supportingText,
    errorMsg,
    btnsProps,
    keepHeaderIconDefaultColors = false,
    isOverlayAboveModal = false,
    isTopLayer = true,
    containerRef,
    children,
    customStyles,
    footerHint
  } = props;

  const dialogRef = containerRef || useRef<HTMLDialogElement>(null);
  const isScrollLockedRef = useRef(false);

  useEffect(() => {
    return () => {
      dialogRef.current?.close();
      releaseScrollLock();
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef?.current;

    if (open) {
      openDialogHandler();
      dialog?.addEventListener('keydown', handleKeydown);
      customEvents.dispatchEvent(new CustomEvent("modal-open", { detail: { currentRef: dialogRef.current, isOverlayAboveModal } }));
    } else {
      closeDialogHandler();
      dialog?.removeEventListener('keydown', handleKeydown);
      customEvents.dispatchEvent(new CustomEvent("modal-close", { detail: { currentRef: dialogRef.current, isOverlayAboveModal } }));
    }

    return () => {
      dialog?.removeEventListener('keydown', handleKeydown);
      releaseScrollLock();
    };
  }, [open, isOverlayAboveModal]);

  const openDialogHandler = () => {
    if (isTopLayer) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.show();
    };

    lockDocumentScroll();
    isScrollLockedRef.current = true;
  };

  const closeDialogHandler = () => {
    dialogRef.current?.close();
    releaseScrollLock();
    onCloseHandler?.();
  };

  const releaseScrollLock = () => {
    if (isScrollLockedRef.current) {
      unlockDocumentScroll();
      isScrollLockedRef.current = false;
    };
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      event.preventDefault();
      onCloseHandler?.();
    }
  };

  onCloseHandler &&
    useClickOutside(dialogRef, onCloseHandler, open && closeOnClickOutside, true);

  return (
    <>
      {!isTopLayer && open && (
        <div
          className={styles['modal__overlay']}
          onClick={closeOnClickOutside ? onCloseHandler : undefined}
        />
      )}
      <dialog
        ref={dialogRef}
        data-modal-is-mobile={isMobile}
        data-modal-position={position}
        data-modal-is-top-layer={isTopLayer}
        className={
          styles['modal'] +
          (wrapperClassName ? ` ${wrapperClassName}` : '')
        }
        style={{ maxWidth, width, ...customStyles }}
      >
        {hasCloseIcon && (
          <button
            className={styles['modal__close-icon']}
            onClick={onCloseHandler}
          >
            {Xmark}
          </button>
        )}

        <ModalHeader
          state={state}
          headerIcon={headerIcon}
          title={title}
          supportingText={supportingText}
          keepIconDefaultColors={keepHeaderIconDefaultColors}
        />

        {children}
        <ModalFooter
          btnsProps={btnsProps}
          isMobile={isMobile}
          errorMsg={errorMsg}
          footerHint={footerHint}
        />
      </dialog>
    </>
  );
};